import type { CartUnavailableProduct } from '@iheartjane/data-access';
import type {
  LoadingEventPayload,
  NavigationEventPayload,
} from '@iheartjane/menu';
import debounce from 'lodash-es/debounce';
import type { DeepReadonly } from 'ts-essentials';

import type { AnalyticsEvent } from '@jane/shared-ecomm/types';
/* eslint-disable-next-line no-restricted-imports */
import type { PendingCartProduct } from '@jane/shared/models';

interface TypedPayloads {
  analyticsEvent: AnalyticsEvent;
  authEvent: {
    name: 'initiateExternalAuth';
    source: 'bag' | 'checkout';
  };
  closeModal?: undefined;
  disableShrinkEveryResize?: undefined;
  enableShrinkEveryResize?: undefined;
  loadingEvent: LoadingEventPayload;
  navigationEvent: NavigationEventPayload;
  openModal?: undefined;
  resizeIFrame?: number;
  resizeIFrameForModal?: number;
  restoreScrollPosition?: unknown;
  saveScrollPosition?: unknown;
  scrollTo: number;
  scrollToTop?: 'smooth';
  unavailableProductsEvent: {
    products: readonly DeepReadonly<PendingCartProduct>[];
    unavailable_products: readonly CartUnavailableProduct[];
  };
}
type MessageType = keyof TypedPayloads;

type Args<T extends MessageType = MessageType> = {
  messageType: T;
  pathname?: string;
} & (object extends Pick<TypedPayloads, T>
  ? // payload declared optional in `TypedPayloads` (`?: unknown`) → optional here
    { payload?: TypedPayloads[T] }
  : // payload declared required → must be provided with its specific type
    { payload: TypedPayloads[T] });

export const postMessageToIframeParent = <T extends MessageType>({
  messageType,
  pathname,
  payload,
}: Args<T>) => {
  const path = pathname || window.location.href;
  window.parent.postMessage({ messageType, payload, pathname: path }, '*');
};

export const debouncedPostMessageToIframeParent = debounce(
  ({ messageType, payload }: Args) =>
    postMessageToIframeParent({
      messageType,
      payload,
    }),
  275
);
